import React from 'react';
import _ from 'lodash';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import { Entity, ctx as L20NContext } from '@sketchpixy/rubix/lib/L20n';
import moment from 'moment';
import * as WearableAPI from '../../_config/wearableAPI';
import * as GatewayAPI from '../../_config/gatewayAPI';
import * as ModalActions from './modal.actions';
import * as UtilsActions from './utils.actions';
import { BACKGROUND_TASK_TYPES, EXPORT_FORMATS } from '../../_config/consts';
import * as formatter from '../../_config/formatter';
import {
  SAVE_WEARABLES,
  APPEND_WEARABLES,
  SAVE_WEARABLES_PAGINATION,
  SET_SELECTED_WEARABLE,
  SET_WEARABLES_FILTER,
  RESET_WEARABLES_FILTERS,
  RESET_WEARABLES_DATA,
  UPDATE_WEARABLE,
  REMOVE_WEARABLE,
  SAVE_WEARABLE_HISTORY,
  APPEND_WEARABLE_HISTORY,
  SAVE_WEARABLE_HISTORY_PAGINATION,
  SET_WEARABLE_HISTORY_FILTER,
  RESET_WEARABLE_HISTORY_FILTER,
  RESET_WEARABLE_HISTORY_DATA,
  SET_WEARABLE_ORDER,
  SET_WEARABLE_OPERATIONAL_MODE,
  SAVE_WEARABLE_ALARMS,
  APPEND_WEARABLE_ALARMS,
  SAVE_WEARABLE_ALARMS_PAGINATION,
  SET_WEARABLE_ALARMS_FILTER,
  RESET_WEARABLE_ALARMS_FILTER,
  RESET_WEARABLE_ALARMS_DATA,
  UPDATE_WEARABLE_ALARM_IN_STATE,
  SAVE_ACTIVE_ALARMS,
} from './actionTypes/wearable';

export function saveWearables(wearables) {
  return {
    type: SAVE_WEARABLES,
    wearables,
  };
}

export function appendWearables(wearables) {
  return {
    type: APPEND_WEARABLES,
    wearables,
  };
}

export function updateWearableInState(wearable) {
  return {
    type: UPDATE_WEARABLE,
    wearable,
  };
}

export function removeWearableFromState(wearableId) {
  return {
    type: REMOVE_WEARABLE,
    wearableId,
  };
}

export function saveWearablesPagination(pagination) {
  return {
    type: SAVE_WEARABLES_PAGINATION,
    pagination,
  };
}

export function setSelectedWearable(wearable) {
  return {
    type: SET_SELECTED_WEARABLE,
    wearable,
  };
}

export function setWearablesFilter(field, value) {
  return {
    type: SET_WEARABLES_FILTER,
    field,
    value
  };
}

export function setWearablesOperationalMode(isOperationalMode) {
  return {
    type: SET_WEARABLE_OPERATIONAL_MODE,
    isOperationalMode,
  };
}
export function resetWearablesFilters() {
  return {
    type: RESET_WEARABLES_FILTERS,
  };
}

export function resetWearablesData() {
  return {
    type: RESET_WEARABLES_DATA,
  };
}

export function saveWearableHistory(history) {
  return {
    type: SAVE_WEARABLE_HISTORY,
    history,
  };
}

export function appendWearableHistory(history) {
  return {
    type: APPEND_WEARABLE_HISTORY,
    history,
  };
}

export function saveWearableHistoryPagination(pagination) {
  return {
    type: SAVE_WEARABLE_HISTORY_PAGINATION,
    pagination,
  };
}

export function setWearableHistoryFilter(field, value) {
  return {
    type: SET_WEARABLE_HISTORY_FILTER,
    field,
    value,
  };
}

export function resetWearableHistoryFilters() {
  return { type: RESET_WEARABLE_HISTORY_FILTER };
}

export function resetWearableHistoryData() {
  return { type: RESET_WEARABLE_HISTORY_DATA };
}

export function setOrder(orderBy, orderDir) {
  return {
    type: SET_WEARABLE_ORDER,
    orderBy,
    orderDir,
  };
}

export function saveWearableAlarms(alarms) {
  return {
    type: SAVE_WEARABLE_ALARMS,
    alarms,
  };
}

export function appendWearableAlarms(alarms) {
  return {
    type: APPEND_WEARABLE_ALARMS,
    alarms,
  };
}

export function saveWearableAlarmsPagination(pagination) {
  return {
    type: SAVE_WEARABLE_ALARMS_PAGINATION,
    pagination,
  };
}

export function setWearableAlarmsFilter(field, value) {
  return {
    type: SET_WEARABLE_ALARMS_FILTER,
    field,
    value,
  };
}

export function resetWearableAlarmsFilters() {
  return { type: RESET_WEARABLE_ALARMS_FILTER };
}

export function resetWearableAlarmsData() {
  return { type: RESET_WEARABLE_ALARMS_DATA };
}

export function updateWearableAlarmInState(alarm) {
  return {
    type: UPDATE_WEARABLE_ALARM_IN_STATE,
    alarm,
  };
}


export function fetchWearables(page = 0, pageSize = 100, append = false) {
  return async (dispatch, getState) => {
    try {
      const { wearables: { data: { filters, sorting } } } = getState();
      const orderBy = sorting.orderBy;
      const orderDir = orderBy ? sorting.orderDir : undefined;
      const params = {
        ...filters,
        page,
        pageSize,
        orderBy,
        orderDir,
      };
      const response = await WearableAPI.fetchWearables(params);
      if (response && response.data && response.data.content) {
        const formattedWearables = _.map(response.data.content, (wearable) => formatter.formatInputData(formatter.WEARABLES, wearable));
        if (append) {
          dispatch(appendWearables(formattedWearables));
        } else {
          dispatch(saveWearables(formattedWearables));
        }
        dispatch(saveWearablesPagination(_.omit(response.data, 'content')));
        return formattedWearables;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchAndAppendWearables(page = 0, pageSize = 100) {
  return async (dispatch, getState) => {
    try {
      const { wearables: { data: { filters, sorting } } } = getState();
      const orderBy = sorting.orderBy;
      const orderDir = orderBy ? sorting.orderDir : undefined;
      const params = {
        ...filters,
        page,
        pageSize,
        orderBy,
        orderDir,
      };
      const response = await WearableAPI.fetchWearables(params);
      if (response && response.data && response.data.content) {
        const formattedWearables = _.map(response.data.content, (wearable) => formatter.formatInputData(formatter.WEARABLES, wearable));
        dispatch(appendWearables(formattedWearables));
        dispatch(saveWearablesPagination(_.omit(response.data, 'content')));
        return formattedWearables;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchAllWearables(page = 0, pageSize = 50) {
  return async (dispatch) => {
    try {
      let params = {
        page,
        pageSize,
      };
      let wearablesResponse = await WearableAPI.fetchWearables(params);
      if (wearablesResponse.data) {
        let pagination = _.omit(wearablesResponse.data, 'content');
        let formattedWearables = _.map(wearablesResponse.data.content, (wearable) => formatter.formatInputData(formatter.WEARABLES, wearable));
        const wearables = [...formattedWearables];
        while (!wearablesResponse.data.last) {
          params = {
            ...params,
            page: wearablesResponse.data.number + 1,
          };
          wearablesResponse = await WearableAPI.fetchWearables(params);
          formattedWearables = _.map(wearablesResponse.data.content, (wearable) => formatter.formatInputData(formatter.WEARABLES, wearable));
          wearables.push(...formattedWearables);
          pagination = _.omit(wearablesResponse.data, 'content');
        }
        dispatch(saveWearablesPagination(pagination));
        dispatch(saveWearables(formattedWearables));
        return wearables;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchWearableById(wearableId) {
  return async (dispatch, getState) => {
    try {
      const response = await WearableAPI.fetchWearableById(wearableId);
      if (response && response.data) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function createWearable(wearableDTO) {
  return async (dispatch, getState) => {
    const formattedWearableDTO = formatter.formatOutputData(formatter.WEARABLES, wearableDTO);
    try {
      const response = await WearableAPI.createWearable(formattedWearableDTO);
      if (response.data) {
        dispatch(updateWearableInState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateWearable(wearableId, wearableDTO) {
  return async (dispatch, getState) => {
    try {
      const response = await WearableAPI.updateWearable(wearableId, wearableDTO);
      if (response.data) {
        dispatch(updateWearableInState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function deleteWearable(wearableId) {
  return async (dispatch, getState) => {
    try {
      const response = await WearableAPI.deleteWearable(wearableId);
      if (response.data) {
        dispatch(removeWearableFromState(wearableId));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function syncGatewayWearables(gatewayId) {
  return async (dispatch, getState) => {
    try {
      const response = await WearableAPI.syncGatewayWearables(gatewayId);
      if (response && response.data !== undefined) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchWearableHistory(wearableId, page = 0, pageSize = 100) {
  return async (dispatch, getState) => {
    try {
      const { wearables: { history: { filters } } } = getState();
      const params = {
        ...filters,
        page,
        pageSize,
      };
      const response = await WearableAPI.fetchWearableHistory(wearableId, params);
      if (response && response.data && response.data.content) {
        dispatch(saveWearableHistory(response.data.content));
        dispatch(saveWearableHistoryPagination(_.omit(response.data, 'content')));
        return response.data.content;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchAndAppendWearableHistory(wearableId, page = 0, pageSize = 100) {
  return async (dispatch, getState) => {
    try {
      const { wearables: { history: { filters } } } = getState();
      const params = {
        ...filters,
        page,
        pageSize,
      };
      const response = await WearableAPI.fetchWearableHistory(wearableId, params);
      if (response && response.data && response.data.content) {
        dispatch(appendWearableHistory(response.data.content));
        dispatch(saveWearableHistoryPagination(_.omit(response.data, 'content')));
        return response.data.content;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchAllWearableHistory(wearableId, page = 0, pageSize = 100) {
  return async (dispatch, getState) => {
    const taskId = moment().valueOf();
    let stopFetching = false;
    const { wearables: { history: { filters } } } = getState();
    dispatch(UtilsActions.addBackgroundTaskSingleton({
      id: taskId,
      type: BACKGROUND_TASK_TYPES.DOWNLOAD_LOCKS_CSV,
      title: 'exportingWearableHistory',
      cancelCallback: () => {
        stopFetching = true;
      },
    }));
    try {
      let params = {
        page,
        pageSize,
        ...filters
      };
      let response = await WearableAPI.fetchWearableHistory(wearableId, params);
      if (response && response.data && response.data.content && !_.isEmpty(response.data.content)) {
        const wearableHistory = [...response.data.content];
        let pagination = _.omit(response.data, 'content');
        while (pagination.number + 1 !== pagination.totalPages && !stopFetching) {
          params = {
            ...params,
            page: pagination.number + 1,
          };
          response = await WearableAPI.fetchWearableHistory(wearableId, params);
          wearableHistory.push(...response.data.content);
          pagination = _.omit(response.data, 'content');
        }
        dispatch(UtilsActions.removeBackgroundTask(taskId));
        return { wearableHistory, stopFetching };
      }
      dispatch(UtilsActions.removeBackgroundTask(taskId));
      throw new Error();
    } catch (error) {
      dispatch(UtilsActions.removeBackgroundTask(taskId));
      throw error;
    }
  };
}

export function exportWearableHistory(wearableId, format = EXPORT_FORMATS.XLSX) {
  return async (dispatch, getState) => {
    try {
      const { wearableHistory, stopFetching } = await dispatch(fetchAllWearableHistory(wearableId));
      if (!stopFetching) {
        const exportData = [];
        _.each(wearableHistory, (event) => {
          exportData.push({
            Timestamp: moment(event.timestamp).format('LLL'),
            'Gateway ID': event.gatewayId || '---',
            RSSI: event.rssi || '---',
            Emergency: event.emergency ? L20NContext.getSync('yes') : L20NContext.getSync('no'),
          });
        });
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = format.toLowerCase();
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = { Sheets: { history: ws }, SheetNames: ['history'] };
        const excelBuffer = XLSX.write(wb, { bookType: format.toLowerCase(), type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, `Wearable_History_${wearableId}.${fileExtension}`);
        return wearableHistory;
      } else {
        throw new Error();
      }
    } catch (error) {
      let errorMessage = 'errorCreatingCSV';
      if (error.message === 'TOO_MANY_TASKS_SAME_TYPE') errorMessage = 'taskAlreadyExecuting';
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity={errorMessage} /></h6>),
        },
      }));
      throw new Error(error);
    }
  };
}

export function fetchWearableAlarms(wearableId = null, page = 0, pageSize = 100) {
  return async (dispatch, getState) => {
    try {
      const { wearables: { alarms: { filters } } } = getState();
      const params = {
        ...filters,
        page,
        pageSize,
      };
      
      // Only add wearableId if provided
      if (wearableId !== null) {
        params.wearableId = wearableId;
      }
      
      const response = await WearableAPI.fetchWearableAlarms(params);
      if (response && response.data && response.data.content) {
        dispatch(saveWearableAlarms(response.data.content));
        dispatch(saveWearableAlarmsPagination(_.omit(response.data, 'content')));
        return response.data.content;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function fetchAndAppendWearableAlarms(wearableId , page = 0, pageSize = 100) {
  return async (dispatch, getState) => {
    try {
      const { wearables: { alarms: { filters } } } = getState();
      const params = {
        wearableId,
        ...filters,
        page,
        pageSize,
      };
      const response = await WearableAPI.fetchWearableAlarms(params);
      if (response && response.data && response.data.content) {
        dispatch(appendWearableAlarms(response.data.content));
        dispatch(saveWearableAlarmsPagination(_.omit(response.data, 'content')));
        return response.data.content;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateWearableAlarm(alarmId, alarmDTO) {
  return async (dispatch, getState) => {
    try {
      const response = await WearableAPI.updateWearableAlarm(alarmId, alarmDTO);
      if (response.data) {
        dispatch(updateWearableAlarmInState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    } 
  };
}

export function saveActiveAlarms(activeAlarms) {
  return {
    type: SAVE_ACTIVE_ALARMS,
    activeAlarms,
  };
}

export function fetchActiveAlarms(pageSize = 100) {
  return async (dispatch, getState) => {
    try {
      const params = {
        status: 'ACTIVE',
        page: 0,
        pageSize,
      };
      const response = await WearableAPI.fetchWearableAlarms(params);
      if (response && response.data && response.data.content) {
        dispatch(saveActiveAlarms(response.data.content));
        return response.data.content;
      }
      return [];
    } catch (error) {
      console.error('Error fetching active alarms:', error);
      dispatch(saveActiveAlarms([]));
      return [];
    }
  };
}

export function exportWearables(format = EXPORT_FORMATS.XLSX) {
  return async (dispatch, getState) => {
    try {
      const wearables = await dispatch(fetchAllWearables());
      const exportData = [];
      
      _.each(wearables, (wearable) => {
        const formattedWearable = formatter.formatInputData(formatter.WEARABLES, wearable);
        const user = wearable.user || {};
        const allowedAreasNames = wearable.allowedAreas && _.isArray(wearable.allowedAreas)
          ? _.map(wearable.allowedAreas, area => area.name).join(', ')
          : '---';
        const notAllowedAreasNames = wearable.notAllowedAreas && _.isArray(wearable.notAllowedAreas)
          ? _.map(wearable.notAllowedAreas, area => area.name).join(', ')
          : '---';
        
        exportData.push({
          'Device ID': wearable.deviceId || '---',
          'Model': wearable.model || '---',
          'Type': wearable.type || '---',
          'User Firstname': user.firstname || '---',
          'User Lastname': user.lastname || '---',
          'User Email': user.email || '---',
          'Battery': wearable.battery !== null && wearable.battery !== undefined ? wearable.battery : '---',
          'Battery Level': formattedWearable.batteryLevel !== null && formattedWearable.batteryLevel !== undefined ? `${formattedWearable.batteryLevel}%` : '---',
          'Emergency': wearable.emergency ? L20NContext.getSync('yes') : L20NContext.getSync('no'),
          'Emergency Timestamp': wearable.emergencyTimestamp ? moment(wearable.emergencyTimestamp).format('LLL') : '---',
          'Allowed Areas': allowedAreasNames,
          'Not Allowed Areas': notAllowedAreasNames,
        });
      });
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = { Sheets: { wearables: ws }, SheetNames: ['wearables'] };
      
      if (format === EXPORT_FORMATS.CSV) {
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        FileSaver.saveAs(blob, 'Wearables_Export.csv');
      } else {
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, 'Wearables_Export.xlsx');
      }
      
      return wearables;
    } catch (error) {
      let errorMessage = 'errorCreatingCSV';
      if (error.message === 'TOO_MANY_TASKS_SAME_TYPE') errorMessage = 'taskAlreadyExecuting';
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity={errorMessage} /></h6>),
        },
      }));
      throw new Error(error);
    }
  };
}


export function fetchAndAppendWearablesAlarms(page = 0, pageSize = 100) {
  return async (dispatch, getState) => {
    try {
      const { wearables: { alarms: { filters } } } = getState();
      const params = {
        ...filters,
        page,
        pageSize,
      };
      const response = await WearableAPI.fetchWearableAlarms(params);
      if (response && response.data && response.data.content) {
        dispatch(appendWearableAlarms(response.data.content));
        dispatch(saveWearableAlarmsPagination(_.omit(response.data, 'content')));
        return response.data.content;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateWearableSettings(settingsDTO) {
  return async (dispatch) => {
    try {
      const formattedSettings = formatter.formatOutputData(formatter.WEARABLE_SETTINGS, settingsDTO);
      const data = {
        type: 'CUSTOM_DOMAIN',
        data: {
          wearables: formattedSettings,
        },
      };
      const response = await GatewayAPI.updateGatewayConfiguration(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

export function updateWearableGatewaySettings(gatewayId, settingsDTO) {
  return async (dispatch) => {
    try {
      const formattedSettings = formatter.formatOutputData(formatter.WEARABLE_SETTINGS, settingsDTO);
      const data = {
        type: 'CUSTOM_GATEWAY',
        gatewayId,
        data: {
          wearables: formattedSettings,
        },
      };
      const response = await GatewayAPI.updateGatewayConfiguration(data);
      if (response && response.data) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw error;
    }
  };
}

